import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DentalHistory, MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';

// Replaces threading `linkedCaseId` hop-by-hop through router `location.state`
// across the intake wizard (/select-patient -> ... -> /confirm-payment). That
// approach silently dropped the id whenever an intermediate page's navigate
// call forgot to spread it forward (e.g. the "complete your profile first"
// detour). Living in Redux (not persist-blacklisted, same as patientForm)
// survives that detour and a reload for free.
// The individual fields extracted from the previous linked case's
// medical_history_snapshot/medical_conditions_snapshot/dental_history_snapshot
// — carried forward so the patient's clinical_data profile is updated with
// them (see completeLinkedCase.ts) since the linked case flow never walks the
// patient through /clinical-data again to re-collect this for the new case.
export interface PreviousClinicalData {
  medicalHistory: MedicalHistory | null;
  medicalConditions: MedicalConditions | null;
  dentalHistory: DentalHistory | null;
}

export interface LinkedCaseState {
  /** The existing upcoming case being completed, instead of creating a new one. */
  linkedCaseId: number | null;
  /** The patient the linked case belongs to — locks SelectPatient's picker to just this one. */
  linkedCasePatientId: number | null;
  // The case's own backend-reported CaseIntakeState.upcoming_payment_required
  // (see getCaseIntake in multiStage.ts) — the ground truth for whether THIS
  // case needs payment, deliberately not derived from the org's pricing
  // config. PaymentInformation.tsx/ConfirmPayment.tsx's own "needs payment"
  // checks are purely pricing-config-derived and go silently false whenever
  // an org has no payment_options configured for a case type, even when a
  // specific linked case genuinely still owes payment — this overrides that.
  linkedCaseNeedsPayment: boolean;
  previousClinicalData: PreviousClinicalData | null;
}

const initialState: LinkedCaseState = {
  linkedCaseId: null,
  linkedCasePatientId: null,
  linkedCaseNeedsPayment: false,
  previousClinicalData: null,
};

const linkedCaseSlice = createSlice({
  name: 'linkedCase',
  initialState,
  reducers: {
    setLinkedCase: (
      state,
      action: PayloadAction<{
        linkedCaseId: number;
        linkedCasePatientId: number | null;
        linkedCaseNeedsPayment: boolean;
        previousClinicalData?: PreviousClinicalData | null;
      }>,
    ) => {
      state.linkedCaseId = action.payload.linkedCaseId;
      state.linkedCasePatientId = action.payload.linkedCasePatientId;
      state.linkedCaseNeedsPayment = action.payload.linkedCaseNeedsPayment;
      state.previousClinicalData = action.payload.previousClinicalData ?? null;
    },
    clearLinkedCase: (state) => {
      state.linkedCaseId = null;
      state.linkedCasePatientId = null;
      state.linkedCaseNeedsPayment = false;
      state.previousClinicalData = null;
    },
  },
});

export const { setLinkedCase, clearLinkedCase } = linkedCaseSlice.actions;
export default linkedCaseSlice.reducer;
